import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Mic, ShoppingCart, Package, Receipt, ArrowRight } from 'lucide-react';
import VoiceInput from '../components/VoiceInput';
import api from '../api/axios';

const VoiceCommands = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const isSwahili = i18n.language === 'sw';

  const processCommand = async (transcript) => {
    setError('');
    const lower = transcript.toLowerCase().trim();

    try {
      // Check for keywords
      if (lower.includes('mauzo') || lower.includes('nauza') || lower.includes('sale') || lower.includes('sell')) {
        // Navigate to sales
        navigate('/sales');
        setResult(isSwahili ? 'Nakuelekeza kwenye mauzo...' : 'Taking you to sales...');
      } 
      else if (lower.includes('gharama') || lower.includes('expense') || lower.includes('cost')) {
        navigate('/expenses');
        setResult(isSwahili ? 'Nakuelekeza kwenye gharama...' : 'Taking you to expenses...');
      }
      else if (lower.includes('bidhaa') || lower.includes('stock') || lower.includes('product')) {
        navigate('/products');
        setResult(isSwahili ? 'Nakuelekeza kwenye bidhaa...' : 'Taking you to products...');
      }
      else if (lower.includes('ripoti') || lower.includes('report')) {
        navigate('/reports');
        setResult(isSwahili ? 'Nakuelekeza kwenye ripoti...' : 'Taking you to reports...');
      }
      else if (lower.includes('mkopo') || lower.includes('loan')) {
        navigate('/loans');
        setResult(isSwahili ? 'Nakuelekeza kwenye mikopo...' : 'Taking you to loans...');
      }
      else if (lower.includes('faida') || lower.includes('profit') || lower.includes('hasara') || lower.includes('loss')) {
        navigate('/ai-insights');
        setResult(isSwahili ? 'Nakuelekeza kwenye uchambuzi...' : 'Taking you to insights...');
      }
      else {
        setResult(isSwahili 
          ? 'Samahani, sikuelewa. Jaribu: "mauzo", "gharama", "bidhaa", "ripoti", "mkopo" au "faida"'
          : 'Sorry, try: "sales", "expenses", "products", "reports", "loans" or "profit"');
      }
    } catch (err) {
      setError(isSwahili ? 'Imetokea tatizo. Jaribu tena.' : 'An error occurred. Try again.');
    }
  };

  const commands = [
    { icon: ShoppingCart, text_sw: '"Nauza" au "Mauzo"', text_en: '"Sell" or "Sales"' },
    { icon: Receipt, text_sw: '"Gharama" au "Expense"', text_en: '"Expense" or "Cost"' },
    { icon: Package, text_sw: '"Bidhaa" au "Stock"', text_en: '"Product" or "Stock"' },
    { icon: ArrowRight, text_sw: '"Ripoti"', text_en: '"Reports"' },
    { icon: ArrowRight, text_sw: '"Mkopo" au "Loan"', text_en: '"Loans"' },
    { icon: ArrowRight, text_sw: '"Faida" au "Hasara"', text_en: '"Profit" or "Loss"' },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-white">
          {isSwahili ? 'Amri za Sauti' : 'Voice Commands'}
        </h2>
        <p className="text-gray-400 text-sm mt-1">
          {isSwahili ? 'Zungumza ili kuendesha mfumo' : 'Speak to navigate the system'}
        </p>
      </div>

      {/* Voice Input Center */}
      <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-8 md:p-12 text-center">
        <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mic size={40} className="text-emerald-400" />
        </div>
        <h3 className="text-white font-bold text-lg mb-2">
          {isSwahili ? 'Bonyeza kuzungumza' : 'Click to Speak'}
        </h3>
        <p className="text-gray-400 text-sm mb-6">
          {isSwahili ? 'Sema jambo unalotaka kufanya' : 'Say what you want to do'}
        </p>
        
        <VoiceInput onResult={processCommand} />

        {result && (
          <div className="mt-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3 text-emerald-400 text-sm">
            {result}
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Available Commands */}
      <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4">
          {isSwahili ? 'Amri Zinazopatikana' : 'Available Commands'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {commands.map((cmd, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
              <cmd.icon size={18} className="text-emerald-400" />
              <div>
                <p className="text-white text-sm">{isSwahili ? cmd.text_sw : cmd.text_en}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VoiceCommands;
